import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import PublicIcon from "@mui/icons-material/Public";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ChatIcon from "@mui/icons-material/Chat";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../pages/Passagens/passagemMpFeeUi";
import { passagemStatusLabel } from "../../pages/Passagens/passagemStatusUi";
import { paymentIntentChipColor, paymentIntentDisplayLabel, paymentIntentTooltip } from "../../pages/Passagens/passagemPaymentIntentUi";

const channelIconBySource = {
  whatsapp: <WhatsAppIcon fontSize="small" />,
  app: <PhoneIphoneIcon fontSize="small" />,
  web: <PublicIcon fontSize="small" />,
  messenger: <ChatIcon fontSize="small" />,
};

function getChannelIcon(source) {
  const key = String(source || "web").toLowerCase();
  return channelIconBySource[key] || <PublicIcon fontSize="small" />;
}

function getInitials(name) {
  return String(name || "C")
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const STATUS_COLOR = {
  emitida: "success",
  paga: "info",
  cancelada: "default",
  estornada: "warning",
  chargeback: "error",
  aguardando_pagamento: "warning",
  em_andamento: "default",
  erro_emissao: "error",
};

export default function PassagemPipelineCard({ card, onOpenVenda, onOpenLead, onRecover, recoverable = false }) {
  const { t } = useTranslation();
  const clientName = card?.clientName || card?.nomePassageiro || t("passagensPage.colClient", { defaultValue: "Cliente" });
  const route = card?.route;
  const value = card?.value;
  const statusKey = String(card?.statusVenda || "").toLowerCase();
  const statusLabel = passagemStatusLabel(t, statusKey);
  const canal = card?.channelName || card?.canalSource || "web";
  const isVenda = card?.kind === "venda" && card?.vendaId;

  const handleOpen = () => {
    if (isVenda) onOpenVenda?.(card.vendaId);
    else if (card?.leadId) onOpenLead?.(card.leadId);
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 0,
        "&:hover": { boxShadow: 2 },
        cursor: isVenda || card?.leadId ? "pointer" : "default",
      }}
      onClick={handleOpen}
    >
      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
          <Stack direction="row" spacing={0.25} alignItems="center" color="text.secondary">
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{card?.createdAtLabel || "—"}</Typography>
          </Stack>
          {(isVenda || card?.leadId) && (
            <Tooltip title={isVenda ? t("passagensPage.detailTitle", { defaultValue: "Detalhe da passagem" }) : t("passagensPage.openConversation", { defaultValue: "Abrir conversa" })}>
              <IconButton size="small" aria-label="abrir" onClick={(e) => { e.stopPropagation(); handleOpen(); }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.light" }}>
            {getInitials(clientName)}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap title={clientName}>
              {clientName}
            </Typography>

            {card?.operatorName && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t("passagensPage.pipelineOperator", { defaultValue: "Operador" })}: <strong>{card.operatorName}</strong>
              </Typography>
            )}

            {route && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap title={route}>
                {route}
              </Typography>
            )}

            <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.75 }}>
              <Chip size="small" icon={getChannelIcon(canal)} label={canal} variant="outlined" />
              {Number.isFinite(Number(value)) && (
                <Chip size="small" color="primary" label={fmtMoney(value)} sx={{ fontWeight: 700 }} />
              )}
              {statusKey && statusKey !== "—" && (
                <Chip size="small" color={STATUS_COLOR[statusKey] || "default"} label={statusLabel} />
              )}
              {card?.paymentIntent?.label && (
                <Tooltip title={paymentIntentTooltip(card.paymentIntent)}>
                  <Chip
                    size="small"
                    color={paymentIntentChipColor(card.paymentIntent.scenario)}
                    label={paymentIntentDisplayLabel(card.paymentIntent)}
                    variant="outlined"
                    sx={{ maxWidth: "100%", height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }}
                  />
                </Tooltip>
              )}
              {card?.localizador && (
                <Chip
                  size="small"
                  icon={<ConfirmationNumberIcon />}
                  label={card.localizador}
                  variant="outlined"
                />
              )}
              {card?.poltrona && (
                <Chip
                  size="small"
                  label={t("passagensPage.pipelineSeat", { defaultValue: "Poltrona" }) + ` ${card.poltrona}`}
                  variant="outlined"
                />
              )}
            </Stack>

            {card?.formaPagamento && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t("passagensPage.colPayment", { defaultValue: "Pagamento" })}: {card.formaPagamento}
              </Typography>
            )}

            {recoverable && card?.leadId && !isVenda && onRecover && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReplayIcon />}
                sx={{ mt: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRecover(card);
                }}
              >
                {t("passagensPage.recoverSale", { defaultValue: "Recuperar venda" })}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
